import React from 'react';
import { Typography, cn } from '@kiiwi/ui';

import { useSelector } from 'react-redux';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { noop, round } from 'lodash';
import { ListMode } from '../SearchResults/types';
import FontAwesomeIcon from '@/components/atoms/FontawesomeIcon/FontawesomeIcon';
import { Tag } from '@/components/ui/not-existing-kiiwi-components/Tag';
import { isDemoAccountSelector } from '@/src/selectors';
import useResponsive from '@/src/hooks/useResponsive';
import SearchCardAvatar from './SearchCardAvatar';
import SearchCardName from './SearchCardName';
import {
  getPostalCodeAndCity,
  getSector,
  getSpecialityName,
} from '@/components/utils/card';
import { useTranslation } from '@/src/i18n';
import { LIMITATION_CENTER } from '@/components/molecules/FilterBar/TLCLimitationField';
import SearchCardTlcCTA from './SearchCardTlcCTA';
import SearchCardTlcUpcomingCTA from './SearchCardTlcUpcomingCTA';

interface Props {
  mode?: ListMode;
  waitingVideoSessions: Array<any>;
  withTheSamePractitioner: boolean;
  item: Required<CardHcd>;
  onClick: React.ReactEventHandler;
  onAvatarClick: (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => void;
}

const SearchCardTlc = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      mode,
      waitingVideoSessions,
      withTheSamePractitioner,
      item,
      onClick,
      onAvatarClick,
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation();
    const { isMobile, isTablet } = useResponsive();
    const isDemoAccount = useSelector(isDemoAccountSelector);
    const hasWaitingVideoSession: boolean = waitingVideoSessions?.length > 0;
    let couldDoTLC: boolean =
      !hasWaitingVideoSession || withTheSamePractitioner;
    if (isDemoAccount && couldDoTLC)
      couldDoTLC = item?.settings?.isDemo === true;
    const disabled = mode && !couldDoTLC;
    const isContainerClickable = (isMobile || isTablet) && !disabled;
    const postalCodeAndCity = getPostalCodeAndCity(item);
    const sectors = item?.publicInformation?.conventionSectors;
    const isUpcomingTlc = mode === 'upcomingTlc';
    const isLimitationCenter = isUpcomingTlc
      ? item?.settings?.tlcAppointmentPatientLimitation === LIMITATION_CENTER
      : item?.settings?.teleconsultationPatientLimitation === LIMITATION_CENTER;

    const handleOnClickContainer = (e: React.SyntheticEvent<Element>) => {
      onClick(e);
    };

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex w-full max-w-[760px] flex-1 gap-4 rounded-lg border border-neutral bg-surface-white p-3',
          isContainerClickable && 'cursor-pointer [&_*]:cursor-pointer',
          disabled &&
            'cursor-not-allowed opacity-50 [&_*]:pointer-events-none [&_*]:cursor-not-allowed',
        )}
        onClick={isContainerClickable ? handleOnClickContainer : noop}
      >
        <div className="hidden sm:block">
          <SearchCardAvatar onClick={onAvatarClick} item={item} />
        </div>
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex gap-2 overflow-hidden">
            <div className="flex-shrink-0 sm:hidden">
              <SearchCardAvatar onClick={onAvatarClick} item={item} size="xs" />
            </div>
            <div className="flex-1 overflow-hidden">
              <SearchCardName item={item} />
              <Typography
                variant="body-1"
                className="text-sm text-neutral-primary sm:text-base"
              >
                {getSpecialityName(item)}
              </Typography>
            </div>
          </div>

          <div className="mt-2">
            {item?.distance && (
              <Typography
                variant="body-1"
                className=" flex items-center text-sm sm:text-base"
              >
                <FontAwesomeIcon
                  name="location-arrow:solid"
                  className="inline h-3 min-h-3 text-neutral-secondary"
                />
                <span className="pl-1 font-semibold text-neutral-secondary">
                  {`${round(item.distance, 1)} km`}
                </span>
              </Typography>
            )}
            <Typography
              variant="body-1"
              className="text-sm text-neutral-secondary sm:text-base"
            >
              {postalCodeAndCity.replace(',', '')}
            </Typography>
            {((sectors && sectors.length > 0) || isLimitationCenter) && (
              <div className="mt-2 flex flex-wrap gap-2">
                {sectors?.map(sector => (
                  <Tag variant="success" size="mobile" key={sector}>
                    {t(getSector(sector))}
                  </Tag>
                ))}

                {isLimitationCenter && (
                  <Tag variant="warning" size="mobile">
                    {t('tlc_limitation_center_warning_2')}
                  </Tag>
                )}
              </div>
            )}
          </div>
        </div>

        {mode === 'tlc' && (
          <SearchCardTlcCTA
            waitingVideoSessions={waitingVideoSessions}
            item={item}
            onClick={onClick}
            withTheSamePractitioner={withTheSamePractitioner}
          />
        )}
        {isUpcomingTlc && <SearchCardTlcUpcomingCTA item={item} />}
      </div>
    );
  },
);

SearchCardTlc.displayName = 'SearchCardTlc';

export default SearchCardTlc;
